import { useMemo, type CSSProperties } from "react";

import { DegradedBanner } from "../app/DegradedBanner";
import { formatPercent } from "../app/format";
import { usePersistentString } from "../app/persistentUi";
import { StreamBadge, Topbar, TopbarClock } from "../app/Topbar";
import { useStreamContext } from "../app/StreamProvider";
import { useBackend } from "../app/BackendProvider";
import { AsyncBlock } from "../admin/bits";
import { useNetworkId } from "../config/ConfigProvider";
import { useResource } from "../app/useResource";
import type { GroupPunctuality, LinePunctuality, PunctualityCounts } from "../contract/types";

/** Bloc de taux + décomptes commun réseau / mode / ligne (D-023, contrat v1.37). */
interface Rates {
  onTimeRatio: number;
  earlyRatio: number;
  lateRatio: number;
  counts: PunctualityCounts;
}

/**
 * Écran Statistiques (backlog §11, P2-37 / P2-34→36, CDC §12.3). Ponctualité :
 *  - **J-1 et jours antérieurs** : rapports FIGÉS (calcul quotidien 3 h, rétention 30 j) ;
 *  - **jour en cours** : ESTIMATION en direct (`provisional`), jamais stockée ;
 *  - **historique mensuel** : agrégats conservés indéfiniment (D4).
 * Lecture seule, aucune écriture.
 */
export function StatsScreen() {
  const { status } = useStreamContext();
  const backend = useBackend();
  const networkId = useNetworkId();

  const daysQuery = useResource((signal) => backend.api.getPunctualityDays(networkId, signal), [backend, networkId]);
  const days = daysQuery.data ?? [];
  const [pickedDay, setPickedDay] = usePersistentString("sentinelle.stats.day");
  const day = useMemo(() => (pickedDay && days.includes(pickedDay) ? pickedDay : (days[1] ?? days[0] ?? null)), [pickedDay, days]);

  const reportQuery = useResource(
    (signal) => (day ? backend.api.getPunctualityReport(networkId, day, {}, signal) : Promise.resolve(null)),
    [backend, networkId, day],
  );
  const monthlyQuery = useResource((signal) => backend.api.getPunctualityMonthly(networkId, {}, signal), [backend, networkId]);

  const report = reportQuery.data;

  return (
    <div className="mae-app">
      <Topbar
        current="stats"
        right={
          <>
            <StreamBadge status={status} />
            <TopbarClock />
          </>
        }
      />
      <DegradedBanner />

      <div className="mae-fill mae-scroll-y" style={{ padding: "20px 24px", background: "var(--sentinelle-surface)" }}>
        <section style={{ marginBottom: 30 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
            <h2 style={{ font: "700 15px/1.2 var(--bulma-family-secondary)", color: "var(--sentinelle-ink)" }}>
              Ponctualité — {report?.provisional ? "estimation du jour" : "rapport figé"}
            </h2>
            <span className="select is-small">
              <label className="is-sr-only" htmlFor="stats-day">
                Jour
              </label>
              <select id="stats-day" value={day ?? ""} onChange={(e) => setPickedDay(e.target.value || null)}>
                {days.map((d, i) => (
                  <option key={d} value={d}>
                    {d}
                    {i === 0 ? " · aujourd'hui (estimation)" : ""}
                  </option>
                ))}
              </select>
            </span>
            {report ? (
              <span className="has-text-grey is-size-7">
                {report.provisional
                  ? "calcul en direct sur l'échantillon partiel — non figé"
                  : report.computedAt
                    ? `figé le ${new Date(report.computedAt).toLocaleString("fr-FR")}`
                    : "figé"}
                {report.period ? ` · régime ${report.period}` : ""}
              </span>
            ) : null}
          </div>

          <AsyncBlock
            loading={(daysQuery.loading && !daysQuery.data) || (reportQuery.loading && !reportQuery.data)}
            error={daysQuery.error ?? reportQuery.error}
            onRetry={() => {
              daysQuery.reload();
              reportQuery.reload();
            }}
            empty={!report}
            emptyLabel="Aucun rapport de ponctualité disponible."
          >
            {report ? (
              <>
                {/* D-023 (contrat v1.37) — modèle 3 bandes : retard toléré (vert,
                    standard §E.3) / avance interdite (rouge) / conforme (neutre).
                    `excluded` (hors bandes) en note, pas de tuile dédiée. */}
                <div style={{ marginBottom: 6, display: "flex", alignItems: "baseline", gap: 10 }}>
                  <span className="ovl">Réseau</span>
                  <span className="has-text-grey is-size-7">
                    {report.network.measuredCount.toLocaleString("fr-FR")} passages mesurés
                    {report.network.counts.excluded > 0
                      ? ` · ${report.network.counts.excluded.toLocaleString("fr-FR")} hors analyse`
                      : ""}
                  </span>
                </div>
                <RatesTiles rates={report.network} style={{ marginBottom: 22 }} />

                <div className="ovl" style={{ marginBottom: 10 }}>
                  Par mode
                </div>
                <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 22 }}>
                  {report.groups.map((g) => (
                    <GroupCard key={g.groupId} group={g} />
                  ))}
                </div>

                <div className="ovl" style={{ marginBottom: 10 }}>
                  Classement des lignes
                </div>
                <LineTable lines={report.lines} network={report.network} />
              </>
            ) : null}
          </AsyncBlock>
        </section>

        <section>
          <h2 style={{ font: "700 15px/1.2 var(--bulma-family-secondary)", color: "var(--sentinelle-ink)", marginBottom: 12 }}>
            Historique mensuel <span className="has-text-grey is-size-7">· conservé sans limite</span>
          </h2>
          <AsyncBlock
            loading={monthlyQuery.loading && !monthlyQuery.data}
            error={monthlyQuery.error}
            onRetry={monthlyQuery.reload}
            empty={(monthlyQuery.data ?? []).length === 0}
            emptyLabel="Aucun mois agrégé."
          >
            {/* Défilement horizontal plutôt qu'illisible sous ~400px (2026-09-11). */}
            <div style={{ overflowX: "auto" }}>
            <table className="table is-fullwidth is-narrow is-hoverable">
              <thead>
                <tr>
                  <th>Mois</th>
                  <th style={{ textAlign: "right" }}>Taux avance</th>
                  <th style={{ textAlign: "right" }}>Taux retard</th>
                  <th style={{ textAlign: "right" }}>Taux conforme</th>
                  <th style={{ textAlign: "right" }}>Passages</th>
                  <th style={{ textAlign: "right" }}>Jours figés</th>
                </tr>
              </thead>
              <tbody>
                {(monthlyQuery.data ?? []).map((m) => (
                  <tr key={m.month}>
                    <td className="mono">{m.month}</td>
                    <td
                      className="mono is-size-7"
                      style={{ textAlign: "right", color: m.earlyRatio > 0 ? "#dc2626" : undefined, fontWeight: m.earlyRatio > 0 ? 700 : undefined }}
                    >
                      {formatPercent(m.earlyRatio, 1)}
                    </td>
                    <td className="mono is-size-7" style={{ textAlign: "right", color: m.lateRatio > 0 ? "#15803d" : undefined }}>
                      {formatPercent(m.lateRatio, 1)}
                    </td>
                    <td className="mono" style={{ textAlign: "right" }}>
                      {formatPercent(m.onTimeRatio, 1)}
                    </td>
                    <td className="mono is-size-7" style={{ textAlign: "right" }}>
                      {m.sampleCount.toLocaleString("fr-FR")}
                    </td>
                    <td className="mono is-size-7" style={{ textAlign: "right" }}>
                      {m.dayCount}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </AsyncBlock>
        </section>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub: string; tone?: string }) {
  return (
    <div>
      <div className="ovl">{label}</div>
      <div className="mono" style={{ fontSize: "1.6rem", fontWeight: 700, color: tone ?? "var(--sentinelle-ink)" }}>
        {value}
      </div>
      <div className="has-text-grey is-size-7">{sub}</div>
    </div>
  );
}

/**
 * 3 tuiles Taux retard / Taux avance / Taux conforme (D-023, contrat v1.37) —
 * standard §E.3 : retard = **toléré** (vert), avance = **interdite** (rouge),
 * conforme = neutre. Sous-titrées par les décomptes bruts (`counts`).
 */
function RatesTiles({ rates, style }: { rates: Rates; style?: CSSProperties }) {
  return (
    <div style={{ display: "flex", gap: 28, flexWrap: "wrap", ...style }}>
      <Kpi
        label="Taux retard"
        value={formatPercent(rates.lateRatio, 1)}
        sub={`${rates.counts.late.toLocaleString("fr-FR")} retard${rates.counts.late > 1 ? "s" : ""}`}
        tone="#15803d"
      />
      <Kpi
        label="Taux avance"
        value={formatPercent(rates.earlyRatio, 1)}
        sub={`${rates.counts.early.toLocaleString("fr-FR")} avance${rates.counts.early > 1 ? "s" : ""}`}
        tone="#dc2626"
      />
      <Kpi
        label="Taux conforme"
        value={formatPercent(rates.onTimeRatio, 1)}
        sub={`${rates.counts.onTime.toLocaleString("fr-FR")} conforme${rates.counts.onTime > 1 ? "s" : ""}`}
      />
    </div>
  );
}

/** Carte compacte d'un mode (Métro / TEOR / FAST / Bus…) — mêmes 3 taux. */
function GroupCard({ group }: { group: GroupPunctuality }) {
  return (
    <div
      style={{
        border: "1px solid var(--sentinelle-rule)",
        background: "#fff",
        borderRadius: 4,
        padding: "10px 14px",
        minWidth: 220,
      }}
    >
      <div style={{ font: "700 13px/1.2 var(--bulma-family-secondary)", color: "var(--sentinelle-ink)", marginBottom: 8 }}>
        {group.label}
      </div>
      <RatesTiles rates={group} />
    </div>
  );
}

function LineTable({ lines, network }: { lines: LinePunctuality[]; network: Rates }) {
  return (
    // Défilement horizontal plutôt qu'illisible sous ~400px (2026-09-11).
    <div style={{ overflowX: "auto" }}>
    <table className="table is-fullwidth is-narrow is-hoverable">
      <thead>
        <tr>
          <th>Ligne</th>
          <th style={{ textAlign: "right" }}>Taux avance</th>
          <th style={{ textAlign: "right" }}>Taux retard</th>
          <th style={{ textAlign: "right" }}>Taux conforme</th>
          <th style={{ textAlign: "right" }}>Écart moyen</th>
          <th style={{ textAlign: "right" }}>Passages</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((l) => (
          <tr key={l.lineId}>
            <td className="mono" style={{ fontWeight: 700 }}>
              {l.lineCode}
            </td>
            <td
              className="mono is-size-7"
              style={{ textAlign: "right", color: l.earlyRatio > 0 ? "#dc2626" : undefined, fontWeight: l.earlyRatio > 0 ? 700 : undefined }}
            >
              {formatPercent(l.earlyRatio, 1)}
            </td>
            <td className="mono is-size-7" style={{ textAlign: "right", color: l.lateRatio > 0 ? "#15803d" : undefined }}>
              {formatPercent(l.lateRatio, 1)}
            </td>
            <td className="mono" style={{ textAlign: "right" }}>
              {formatPercent(l.onTimeRatio, 1)}
            </td>
            <td
              className="mono is-size-7"
              style={{ textAlign: "right", color: l.averageDeviationSeconds < 0 ? "#dc2626" : undefined }}
            >
              {l.averageDeviationSeconds > 0 ? "+" : ""}
              {Math.round(l.averageDeviationSeconds / 6) / 10} min
            </td>
            <td className="mono is-size-7" style={{ textAlign: "right" }}>
              {l.sampleCount.toLocaleString("fr-FR")}
            </td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td className="mono" style={{ fontWeight: 700 }}>
            Total
          </td>
          <td className="mono is-size-7" style={{ textAlign: "right", fontWeight: 700 }}>
            {formatPercent(network.earlyRatio, 1)}
          </td>
          <td className="mono is-size-7" style={{ textAlign: "right", fontWeight: 700 }}>
            {formatPercent(network.lateRatio, 1)}
          </td>
          <td className="mono" style={{ textAlign: "right", fontWeight: 700 }}>
            {formatPercent(network.onTimeRatio, 1)}
          </td>
          <td colSpan={2} />
        </tr>
      </tfoot>
    </table>
    </div>
  );
}
